import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  BOARD_EVAL_BAR_RESERVE_PX,
  MIN_BOARD_WIDTH,
  MOBILE_VIEWPORT_BOTTOM_PAD_PX,
  TRAINER_BOARD_VIEWPORT_BOTTOM_RESERVE_PX,
  TRAINER_SIDE_COLUMN_MIN_RESERVE_PX,
  responsiveMaxBoardWidth,
  TABLET_LANDSCAPE_TRAINER_BOARD_WIDTH,
  TABLET_MAX_BOARD_WIDTH,
  ULTRA_WIDE_BOARD_WIDTH_SCALE,
  LARGE_DESKTOP_BOARD_WIDTH_SCALE,
  WIDE_DESKTOP_BOARD_WIDTH_SCALE,
  type ResponsiveMaxBoardWidthOptions,
} from './boardLayoutConstants';
import { useTrainerPanelMeasureRef } from './TrainerPanelLayout';
import { useLandscapeLayout } from './useLandscapeLayout';

export type UseFittedBoardWidthOptions = ResponsiveMaxBoardWidthOptions & {
  /** Width reserved by puzzle controls when placed beside the board (fallback before measure). */
  besideControlsReservePx?: number;
  /** Vertical reserve for stacked controls below the board (fallback before measure). */
  stackedControlsReservePx?: number;
  /** Cap board size by the measure container height (fill-height trainer layouts). */
  fitToContainerHeight?: boolean;
  /** Grow the board up to this share of the measure container width (e.g. review at 50%). */
  targetViewportWidthFraction?: number;
  /** Vertical reserve below the board when controls sit beside it (fallback before measure). */
  besideBottomReservePx?: number;
};

/** Prefer a live chrome measurement; fall back to the hand-tuned constant. */
export const resolveChromeReservePx = (
  measuredPx: number,
  fallbackPx: number,
): number => (measuredPx > 0 ? measuredPx : fallbackPx);

export const fitBoardWidth = (
  containerWidth: number,
  maxBoardWidth: number,
  besideControlsReservePx = 0,
  maxBoardHeight?: number,
): number => {
  const available = Math.max(0, containerWidth - besideControlsReservePx);
  let fitted = Math.min(maxBoardWidth, available);
  if (maxBoardHeight != null && maxBoardHeight > 0) {
    fitted = Math.min(fitted, maxBoardHeight);
  }
  return Math.max(MIN_BOARD_WIDTH, Math.floor(fitted));
};

/** Viewport width for clamping before the measure container has real layout. */
export const viewportClientWidth = (): number => {
  if (typeof document !== 'undefined') {
    return document.documentElement.clientWidth;
  }
  if (typeof window !== 'undefined') {
    return window.innerWidth;
  }
  return 0;
};

/** Avoid painting at tablet max (960) on narrower iPad portrait before measure runs. */
export const initialFittedBoardWidth = (maxBoardWidth: number): number => {
  const viewportWidth = viewportClientWidth();
  if (viewportWidth <= 0) {
    return maxBoardWidth;
  }
  return Math.max(
    MIN_BOARD_WIDTH,
    Math.min(maxBoardWidth, viewportWidth),
  );
};

/**
 * Height budget for a square board inside a fill-height trainer board region
 * (excludes the stacked info header above the region).
 */
export const fitBoardHeightBudget = (
  boardRegionHeight: number,
  stackedControlsReservePx = 0,
): number =>
  boardRegionHeight -
  BOARD_EVAL_BAR_RESERVE_PX -
  stackedControlsReservePx -
  MOBILE_VIEWPORT_BOTTOM_PAD_PX;

export const effectiveMaxBoardWidth = (
  cappedMax: number,
  containerWidth: number,
  targetViewportWidthFraction?: number,
): number => {
  if (targetViewportWidthFraction == null) {
    return cappedMax;
  }
  const viewportWidth =
    typeof window !== 'undefined' ? window.innerWidth : 0;
  const widthBasis = Math.max(containerWidth, viewportWidth);
  if (widthBasis <= 0) {
    return cappedMax;
  }
  return Math.max(
    cappedMax,
    Math.floor(widthBasis * targetViewportWidthFraction),
  );
};

/** Shrinks the chessboard to the measured column width, with a phone cap. */
export const useFittedBoardWidth = (
  maxBoardWidth: number,
  options?: UseFittedBoardWidthOptions,
) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Trainer layouts reserve vertical space for the header/controls and cap the
  // board by the remaining height, so the fixed phone width cap is unnecessary
  // there — let the board fill the full container width instead.
  const heightFitActive =
    (options?.fitToContainerHeight ?? false) ||
    (options?.stackedControlsReservePx ?? 0) > 0;
  const cappedMax =
    isMobile && heightFitActive && options?.mobileMaxBoardWidth == null
      ? maxBoardWidth
      : responsiveMaxBoardWidth(maxBoardWidth, isMobile, options);
  const besideControlsReservePx = options?.besideControlsReservePx ?? 0;
  const stackedControlsReservePx = options?.stackedControlsReservePx ?? 0;
  const besideBottomReservePx =
    options?.besideBottomReservePx ?? TRAINER_BOARD_VIEWPORT_BOTTOM_RESERVE_PX;
  const fitToContainerHeight = options?.fitToContainerHeight ?? false;
  const targetViewportWidthFraction = options?.targetViewportWidthFraction;
  const panelMeasureRef = useTrainerPanelMeasureRef();
  const fallbackContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = panelMeasureRef ?? fallbackContainerRef;
  const boardAnchorRef = useRef<HTMLDivElement>(null);
  const chromeBelowNodeRef = useRef<HTMLDivElement | null>(null);
  const chromeBesideNodeRef = useRef<HTMLDivElement | null>(null);
  const chromeResizeObserverRef = useRef<ResizeObserver | null>(null);
  const [boardWidth, setBoardWidth] = useState(() =>
    initialFittedBoardWidth(cappedMax),
  );

  const readChromeBelowPx = () => {
    const h = chromeBelowNodeRef.current?.getBoundingClientRect().height ?? 0;
    return h > 0 ? Math.round(h) : 0;
  };
  const readChromeBesidePx = () => {
    const w = chromeBesideNodeRef.current?.getBoundingClientRect().width ?? 0;
    return w > 0 ? Math.round(w) : 0;
  };

  const updateBoardWidth = useCallback(() => {
    const container = containerRef.current;
    const viewportWidth = viewportClientWidth();
    const measuredBelowPx = readChromeBelowPx();
    const measuredBesidePx = readChromeBesidePx();
    const stackedReserve = resolveChromeReservePx(
      measuredBelowPx,
      stackedControlsReservePx,
    );
    const besideReserve = resolveChromeReservePx(
      measuredBesidePx,
      besideControlsReservePx,
    );
    // When stacked, measured below covers drill+scrubber; when beside, it is
    // scrubber/status only and replaces the beside-bottom fallback.
    const bottomReserve =
      stackedControlsReservePx > 0
        ? stackedReserve
        : resolveChromeReservePx(measuredBelowPx, besideBottomReservePx);
    if (!container) {
      // Still clamp to the viewport so tablet portrait never paints at 960px
      // while the measure shell is missing (or not yet attached).
      if (viewportWidth > 0) {
        setBoardWidth(initialFittedBoardWidth(cappedMax));
      }
      return;
    }
    const { width: containerWidth, height } = container.getBoundingClientRect();
    // Prefer the board column when controls are stacked — avoids over-wide
    // measure after TrainerPanelLayout remounts (e.g. replay loading → loaded).
    // When controls sit beside the board, keep the full container width so the
    // beside reserve is not double-subtracted.
    const anchorWidth =
      boardAnchorRef.current?.getBoundingClientRect().width ?? 0;
    const measuredWidth =
      besideReserve === 0 && anchorWidth > 0
        ? Math.min(anchorWidth, containerWidth)
        : containerWidth;
    // Always bind by the layout viewport — flex min-content can inflate the
    // measure shell to the current (oversized) board on first paint.
    const widthBasis =
      viewportWidth > 0
        ? Math.min(measuredWidth, viewportWidth)
        : measuredWidth;
    const width =
      besideReserve > 0
        ? Math.min(
            widthBasis,
            // Leave equal min gutters so leftover after board+controls can
            // center the board and still fit the left info panel.
            Math.max(
              0,
              viewportWidth - 2 * TRAINER_SIDE_COLUMN_MIN_RESERVE_PX,
            ),
          )
        : widthBasis;
    let maxBoardHeight: number | undefined;

    if (targetViewportWidthFraction != null && typeof window !== 'undefined') {
      const verticalReserve =
        BOARD_EVAL_BAR_RESERVE_PX +
        bottomReserve +
        MOBILE_VIEWPORT_BOTTOM_PAD_PX;

      // Fill-height trainer panels vertically center the board, so boardTop-based
      // viewport math under-estimates available space and collapses the board.
      if (fitToContainerHeight && height > 0) {
        const heightCap = height - verticalReserve;
        if (heightCap > 0) {
          maxBoardHeight = heightCap;
        }
      } else {
        // Content-sized shells (puzzles / review): width fraction alone overflows
        // short landscape viewports (e.g. iPad) unless height is capped too.
        const viewportHeight =
          typeof document !== 'undefined'
            ? document.documentElement.clientHeight
            : window.innerHeight;
        const boardTop = Math.max(
          0,
          boardAnchorRef.current?.getBoundingClientRect().top ??
            container.getBoundingClientRect().top,
        );
        const viewportMax = viewportHeight - boardTop - verticalReserve;
        if (viewportMax > 0) {
          maxBoardHeight = viewportMax;
        }
      }
    } else if (fitToContainerHeight) {
      // Prefer the flex board region so stacked course info above the board is
      // excluded. Region height is parent-flex allocated (not content-sized), so
      // this avoids the boardTop centering feedback loop on short landscapes.
      // When height is still 0 on the first pass (common before fill-height flex
      // settles), fall through to the viewport budget below.
      const boardRegion = container.querySelector('[data-trainer-board-region]');
      const regionHeight =
        boardRegion instanceof HTMLElement
          ? boardRegion.getBoundingClientRect().height
          : height;
      let heightCap =
        regionHeight > 0 || height > 0
          ? fitBoardHeightBudget(
              regionHeight > 0 ? regionHeight : height,
              bottomReserve,
            )
          : 0;
      if (typeof window !== 'undefined') {
        const containerTop = Math.max(
          0,
          container.getBoundingClientRect().top,
        );
        const viewportBudget = fitBoardHeightBudget(
          window.innerHeight - containerTop,
          bottomReserve,
        );
        if (viewportBudget > 0) {
          heightCap =
            heightCap > 0 ? Math.min(heightCap, viewportBudget) : viewportBudget;
        }
      }
      if (heightCap > 0) {
        maxBoardHeight = heightCap;
      }
    } else if (stackedControlsReservePx > 0 || measuredBelowPx > 0) {
      // Viewport fitting avoids a feedback loop when the measure ref sits on a
      // content-sized inner box (tablet portrait review / line trainers).
      const viewportHeight =
        typeof document !== 'undefined'
          ? document.documentElement.clientHeight
          : typeof window !== 'undefined'
            ? window.innerHeight
            : 0;
      if (viewportHeight > 0) {
        const boardTop = Math.max(
          0,
          boardAnchorRef.current?.getBoundingClientRect().top ??
            container.getBoundingClientRect().top,
        );
        const viewportMax =
          viewportHeight -
          boardTop -
          BOARD_EVAL_BAR_RESERVE_PX -
          bottomReserve -
          MOBILE_VIEWPORT_BOTTOM_PAD_PX;
        if (viewportMax > 0) {
          maxBoardHeight = viewportMax;
        }
      } else if (height > 0) {
        maxBoardHeight = height - bottomReserve;
      }
    }
    setBoardWidth(
      fitBoardWidth(
        width,
        effectiveMaxBoardWidth(cappedMax, width, targetViewportWidthFraction),
        besideReserve,
        maxBoardHeight,
      ),
    );
  }, [
    besideBottomReservePx,
    besideControlsReservePx,
    cappedMax,
    fitToContainerHeight,
    stackedControlsReservePx,
    targetViewportWidthFraction,
  ]);

  const observeChromeNode = useCallback(
    (node: HTMLDivElement | null, slot: 'below' | 'beside') => {
      const prev =
        slot === 'below' ? chromeBelowNodeRef.current : chromeBesideNodeRef.current;
      const observer = chromeResizeObserverRef.current;
      if (prev && observer && prev !== node) {
        observer.unobserve(prev);
      }
      if (slot === 'below') {
        chromeBelowNodeRef.current = node;
      } else {
        chromeBesideNodeRef.current = node;
      }
      if (node && observer) {
        observer.observe(node);
      }
      updateBoardWidth();
    },
    [updateBoardWidth],
  );

  const chromeBelowRef = useCallback(
    (node: HTMLDivElement | null) => {
      observeChromeNode(node, 'below');
    },
    [observeChromeNode],
  );
  const chromeBesideRef = useCallback(
    (node: HTMLDivElement | null) => {
      observeChromeNode(node, 'beside');
    },
    [observeChromeNode],
  );

  useLayoutEffect(() => {
    updateBoardWidth();

    // Fill-height flex often allocates region height after the first layout pass;
    // a double rAF remasure catches the settled size without waiting for rotate.
    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        updateBoardWidth();
      });
    });

    const container = containerRef.current;
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateBoardWidth)
        : null;
    chromeResizeObserverRef.current = observer;
    if (observer && container) {
      observer.observe(container);
      const boardAnchor = boardAnchorRef.current;
      if (boardAnchor) {
        observer.observe(boardAnchor);
      }
      const boardRegion = container.querySelector('[data-trainer-board-region]');
      if (boardRegion) {
        observer.observe(boardRegion);
      }
      if (chromeBelowNodeRef.current) {
        observer.observe(chromeBelowNodeRef.current);
      }
      if (chromeBesideNodeRef.current) {
        observer.observe(chromeBesideNodeRef.current);
      }
    }
    window.addEventListener('resize', updateBoardWidth);
    window.addEventListener('orientationchange', updateBoardWidth);
    window.visualViewport?.addEventListener('resize', updateBoardWidth);
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      observer?.disconnect();
      chromeResizeObserverRef.current = null;
      window.removeEventListener('resize', updateBoardWidth);
      window.removeEventListener('orientationchange', updateBoardWidth);
      window.visualViewport?.removeEventListener('resize', updateBoardWidth);
    };
    // Re-attach when TrainerPanelLayout remounts with a new measure ref object.
  }, [updateBoardWidth, panelMeasureRef]);

  return {
    containerRef,
    boardAnchorRef,
    chromeBelowRef,
    chromeBesideRef,
    boardWidth,
    maxBoardWidth: cappedMax,
  };
};

export type UseTrainerFittedBoardWidthOptions = {
  designBoardWidth: number;
  /** Horizontal space for controls when they sit beside the board on tablet layouts. */
  besideControlsReservePx?: number;
  /** Vertical space for caption + controls when they stack below the board. */
  stackedControlsReservePx?: number;
  /** Grow the board up to the trainer panel height on desktop. */
  fitToContainerHeight?: boolean;
  /** Target board width as a fraction of the measure container (review fills ~half the screen). */
  targetViewportWidthFraction?: number;
  /** Vertical reserve below the board when controls sit beside it (caption-only boards pass a small value). */
  besideBottomReservePx?: number;
  /**
   * Override side-vs-stacked control layout. Puzzle-kit boards still stack until
   * desktopMin (1194); trainers put controls beside from md (1024).
   */
  controlsBeside?: boolean;
  /** Keep the phone width cap even when height-fitting (puzzles / light chrome). */
  mobileMaxBoardWidth?: number;
};

/** Side controls in landscape — stacked in portrait. */
export const useTrainerControlsBeside = () => useLandscapeLayout();

/** Fills tablet-width trainer columns; keeps the design width on large desktop layouts. */
export const useTrainerFittedBoardWidth = ({
  designBoardWidth: designWidth,
  besideControlsReservePx = 0,
  stackedControlsReservePx = 0,
  fitToContainerHeight = false,
  targetViewportWidthFraction,
  besideBottomReservePx,
  controlsBeside,
  mobileMaxBoardWidth,
}: UseTrainerFittedBoardWidthOptions) => {
  const theme = useTheme();
  const defaultControlsBeside = useTrainerControlsBeside();
  const isControlsBeside = controlsBeside ?? defaultControlsBeside;
  const isUltraWide = useMediaQuery(theme.breakpoints.up('xxxl'));
  const isWideDesktop = useMediaQuery(theme.breakpoints.up('xxl'));
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('xl'));
  // Compact: tablet landscape through xl (controls + info beside the board).
  const isCompactDesktop = useMediaQuery(theme.breakpoints.between('md', 'xl'));

  const scaledDesignWidth = isUltraWide
    ? Math.round(designWidth * ULTRA_WIDE_BOARD_WIDTH_SCALE)
    : isWideDesktop
      ? Math.round(designWidth * WIDE_DESKTOP_BOARD_WIDTH_SCALE)
      : isLargeDesktop
        ? Math.round(designWidth * LARGE_DESKTOP_BOARD_WIDTH_SCALE)
        : designWidth;

  const maxBoardWidth = isLargeDesktop
    ? scaledDesignWidth
    : isCompactDesktop
      ? TABLET_LANDSCAPE_TRAINER_BOARD_WIDTH
      : TABLET_MAX_BOARD_WIDTH;

  const fitted = useFittedBoardWidth(maxBoardWidth, {
    besideControlsReservePx: isControlsBeside ? besideControlsReservePx : 0,
    stackedControlsReservePx: isControlsBeside ? 0 : stackedControlsReservePx,
    // Courses use fill-height shells on tablet portrait too; height-fit
    // even when drill controls stack under the board.
    fitToContainerHeight,
    targetViewportWidthFraction: isControlsBeside
      ? targetViewportWidthFraction
      : undefined,
    besideBottomReservePx,
    mobileMaxBoardWidth,
  });

  return { ...fitted, isControlsBeside };
};
